import { Page1 } from "./Pages/Page1";
import { Page2 } from "./Pages/Page2";
import { Page3 } from "./Pages/Page3";
import { Typography } from "@foodpilot/foods";

import { useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import { useUpsertOrganizationSupplier } from "src/api/client-api/company/useUpsertOrganizationSupplier";
import { Company } from "src/api/client-api/company/useGetOneOrganization.ts";
import { useRef, useState } from "react";
import { SupplierFormType } from "./SupplierForm.types";
import { SupplierService } from "../SupplierService";
import { makeLinksBaseline, saveSupplierWithLinks, type SupplierLinksSyncState } from "../supplierLinksSync";
import {
    useCreateIngredientSupplierLink,
    useDeleteIngredientSupplierLink,
} from "src/api/client-api/ingredientSupplier/useIngredientSupplierLinks";
import { getApiClientErrorMessage } from "src/utils/apiViolations";
import { useFoodpilotContext } from "src/context/FoodpilotContext";
import { URLHandler } from "src/services/URLHandler";
import { useSaveIngredientSupplierTransports } from "src/components/Transport/IngredientSupplierTransportFields";
import { INGREDIENT_LEVEL_STAGES } from "src/api/client-api/transport/transportStages";
import { useEnabledTransportStages } from "src/api/client-api/transport/useEnabledTransportStages";
import { useSnackbar } from "src/utils/useSnackbar";
import { hasMissingRequiredIngredientSupplierTransportSelections } from "src/components/Transport/ingredientSupplierTransportPersistence";
import { useIngredientSupplierTransportSubmission } from "src/components/Transport/useIngredientSupplierTransportSubmission";
import { EntityFormPage, EntityQuitConfirmDialog, FormHeader } from "src/components/EntityForm";
import { useIsProductUxV2 } from "src/hooks/useIsProductUxV2";
import { SupplierFormLegacyView } from "./SupplierFormLegacyView";

type SupplierFormProps = {
    existingSupplier?: Company;
    startingPageId: string;
};

export const SupplierForm = (props: SupplierFormProps) => {
    const { selectedCompany } = useFoodpilotContext();
    const { existingSupplier, startingPageId } = props;
    const isProductUxV2 = useIsProductUxV2();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const upsertSupplier = useUpsertOrganizationSupplier();
    const createLink = useCreateIngredientSupplierLink();
    const deleteLink = useDeleteIngredientSupplierLink();
    const saveIngredientSupplierTransports = useSaveIngredientSupplierTransports();
    const { isStageEnabled } = useEnabledTransportStages();
    const snackbar = useSnackbar();

    const formData = SupplierService.getSupplierToCreate(existingSupplier);
    const [supplier, setSupplierState] = useState<SupplierFormType>(formData);
    const [quitDialogOpen, setQuitDialogOpen] = useState(false);
    const isNew = existingSupplier === undefined;
    const linksSyncState = useRef<SupplierLinksSyncState | null>(null);
    const lastApiErrorMessage = useRef<string | null>(null);
    const supplierListUrl = URLHandler.supplier.list(selectedCompany?.id ?? 0);
    const notifyPersistenceError = () => {
        const apiMessage = lastApiErrorMessage.current;
        lastApiErrorMessage.current = null;
        snackbar.setSnackbarProps({
            type: "error",
            message: apiMessage ?? t("une_erreur_est_survenue", "An error occurred"),
        });
    };
    const notifyTransportError = () => {
        snackbar.setSnackbarProps({
            type: "error",
            message: t(
                "ingredientSupplier.transport.saveError",
                "The transport circuits could not be saved. Please try again.",
            ),
        });
    };
    const submission = useIngredientSupplierTransportSubmission({
        draft: supplier,
        setDraftState: setSupplierState,
        relationsKey: "ingredientSuppliers",
        relatedResourceKey: "ingredient",
        getDraftCounterpartIri: (row) => row.ingredient["@id"],
        upsert: async (draft) => {
            if (selectedCompany === null) {
                return Promise.reject(new Error("No selected company."));
            }
            linksSyncState.current ??= {
                baseline: makeLinksBaseline(formData.ingredientSuppliers),
            };
            lastApiErrorMessage.current = null;
            try {
                return await saveSupplierWithLinks({
                    draft,
                    selectedCompanyIri: selectedCompany["@id"],
                    state: linksSyncState.current,
                    saveOrganization: (organizationDraft) =>
                        upsertSupplier.mutateAsync({
                            company: SupplierService.supplierFormToCompanyWrite(organizationDraft),
                            organizationId: selectedCompany.id,
                        }),
                    createLink: (link) => createLink.mutateAsync({ ...link, organizationId: selectedCompany.id }),
                    deleteLink: (link) => deleteLink.mutateAsync({ ...link, organizationId: selectedCompany.id }),
                });
            } catch (error) {
                lastApiErrorMessage.current = getApiClientErrorMessage(error);
                throw error;
            }
        },
        saveTransportSelections: saveIngredientSupplierTransports,
        onSuccess: () => navigate(supplierListUrl),
        // FoodsFormBase closes its legacy dialog after invoking this callback.
        // Flush the route update so that batched dialog state cannot keep the discarded form rendered.
        onExit: () => navigate(supplierListUrl, { flushSync: true, replace: true }),
        onPersistenceError: notifyPersistenceError,
        onTransportError: notifyTransportError,
        onConcurrentEdit: notifyPersistenceError,
    });

    if (selectedCompany === null || (!isProductUxV2 && Number.isNaN(Number.parseInt(startingPageId)))) {
        return (
            <Typography variant="big-bold">
                {t("supplier.form.page_not_found", "The page you're trying access does not exist")}
            </Typography>
        );
    }

    const subtitle = supplier.clientId ? `${supplier.name || ""} - ${supplier.clientId}` : `${supplier.name || ""}`;
    const formTitle = isNew ? t("creation_d_un_nouveau_fournisseur") : t("modifier_le_fournisseur");
    const enabledStages = INGREDIENT_LEVEL_STAGES.filter((stage) => isStageEnabled(stage));

    const hasMissingTransportSelections = () => {
        if (enabledStages.length === 0) {
            return false;
        }

        return hasMissingRequiredIngredientSupplierTransportSelections(supplier.ingredientSuppliers, enabledStages);
    };

    const handleValidate = async (): Promise<boolean> => {
        if (!supplier.name || !supplier.clientId) {
            snackbar.setSnackbarProps({
                type: "error",
                message: t("form.invalidstep", "Complete the required fields."),
            });
            return false;
        }

        if (hasMissingTransportSelections()) {
            snackbar.setSnackbarProps({
                type: "error",
                message: t(
                    "ingredientSupplier.transport.missingRequiredIngredients",
                    "Enter the required transport circuits for each ingredient.",
                ),
            });
            return false;
        }

        return await submission.submit();
    };

    if (!isProductUxV2) {
        return (
            <SupplierFormLegacyView
                existingSupplier={existingSupplier}
                isNew={isNew}
                isSaving={submission.isSubmitting || upsertSupplier.isLoading}
                onConfirmExit={() => void submission.confirmExit()}
                onValidate={handleValidate}
                setSupplier={submission.setDraft}
                startingPageId={startingPageId}
                subtitle={subtitle}
                supplier={supplier}
            />
        );
    }

    return (
        <>
            <EntityQuitConfirmDialog
                open={quitDialogOpen}
                title={
                    isNew ?
                        t("etes_vous_sur_de_vouloir_quitter_la_creation_du_fournisseur")
                    :   t("etes_vous_sur_de_vouloir_quitter_la_modification_du_fournisseur")
                }
                confirmLabel={isNew ? t("common.close", "Close") : t("ne_pas_enregistrer_et_quitter")}
                cancelLabel={isNew ? t("ne_pas_quitter_la_creation") : t("ne_pas_quitter_la_modification")}
                onConfirm={() => void submission.confirmExit()}
                onCancel={() => setQuitDialogOpen(false)}
                onDismiss={() => setQuitDialogOpen(false)}
            />
            <EntityFormPage
                header={(ref) => (
                    <FormHeader
                        ref={ref}
                        breadcrumbs={[
                            { label: t("suppliers", "Suppliers"), action: () => setQuitDialogOpen(true) },
                            { label: formTitle },
                        ]}
                        title={formTitle}
                        subtitle={subtitle || undefined}
                        onCancel={() => setQuitDialogOpen(true)}
                        onSave={() => void handleValidate()}
                        saving={submission.isSubmitting || upsertSupplier.isLoading}
                        saveDisabled={!supplier.name || !supplier.clientId}
                        testIdPrefix="supplier-v2"
                    />
                )}
            >
                <Page1
                    appearance="product-v2"
                    supplier={supplier}
                    setSupplier={submission.setDraft}
                    existingSupplier={existingSupplier}
                    progress={{ current: 1, total: 3 }}
                />
                <Page2
                    appearance="product-v2"
                    supplier={supplier}
                    setSupplier={submission.setDraft}
                    existingSupplier={existingSupplier}
                    progress={{ current: 2, total: 3 }}
                />
                <Page3
                    appearance="product-v2"
                    supplier={supplier}
                    setSupplier={submission.setDraft}
                    existingSupplier={existingSupplier}
                    progress={{ current: 3, total: 3 }}
                    showTransport={enabledStages.length > 0}
                />
            </EntityFormPage>
        </>
    );
};
